import { useState, useEffect } from 'react';
import { ContentPageData } from '../useContentPageData/useContentPageData';

export interface MatchedPage {
  presentationPageNumber: number,
  matches: string[],
  s3Key: string,
}

const textSearch = (data: ContentPageData[], query: string): MatchedPage[] => {
  const totalMatches: MatchedPage[] = [];
  if (!query || query.length < 1) return totalMatches;
  const nq = query.toLowerCase();
  data.forEach((page: ContentPageData) => {
    const matches: string[] = [];

    // Match against title
    if (page.title && page.title.toLowerCase().includes(nq)) {
      matches.push(`Title: ${page.title}`);
    }

    // Match against content
    if (page.content) {
      const textWithoutLineBreaks = page.content.replace(/(?:\r\n|\r|\n)/g, ' ');
      const text = textWithoutLineBreaks.toLowerCase();
      let index = text.indexOf(nq);
      while (index !== -1) {
        matches.push(getTextBlurb(textWithoutLineBreaks, query, index));
        index = text.indexOf(nq, index + 1);
      }
    }

    if (matches.length) {
      totalMatches.push({
        presentationPageNumber: page.presentationPageNumber,
        s3Key: page.s3Key,
        matches,
      });
    }
  });

  return totalMatches;
}

const getTextBlurb = (
  text: string,
  str: string,
  index: number,
): string => {
  const start = Math.max(0, index - 30);
  const end = Math.min(text.length, index + str.length + 30);

  const prefix = start > 0 ? '...' : '';
  const body = text.substring(start, end);
  const suffix = end < text.length ? '...' : '';

  return prefix + body + suffix;
}

/**
 * Function for searching text in a presentable's pages content
 * @param presentable: Presentable - Contains the pages to search over
 * @param searchText: string - Text to find in presentable's pages
 * @returns { matches: MatchedPage[], totalMatchCount: number }
 */
const useFileTextSearch = (
  searchText?: string,
  pageData?: ContentPageData[],
): { matches: MatchedPage[]; totalMatchCount: number } => {
  const [matches, setMatches] = useState<MatchedPage[]>([]);
  const totalMatchCount = matches.reduce((acc, page) => acc + page.matches.length, 0);

  // PERFORMS THE SEARCH UPON THE SEARCHTEXT BEING UPDATED
  useEffect(() => {
    if (!pageData || !searchText) {
      setMatches([]);
      return;
    }

    const normalized = (searchText || '').toLowerCase();
    const found = textSearch(pageData, normalized);
    setMatches(found);
  }, [searchText, pageData]);

  return {
    matches,
    totalMatchCount,
  }
}

export default useFileTextSearch;
